import type { AudioChunk } from "../types";

interface RecorderOptions {
  chunkSeconds: number;
  onChunk: (chunk: AudioChunk) => void;
  onLevel: (rms: number) => void;
}

const TARGET_SAMPLE_RATE = 16_000;

export class PcmChunkRecorder {
  private audioContext?: AudioContext;
  private source?: MediaStreamAudioSourceNode;
  private node?: AudioWorkletNode;
  private zeroGain?: GainNode;
  private stream?: MediaStream;
  private onChunk?: (chunk: AudioChunk) => void;
  private onLevel?: (rms: number) => void;

  async start(options: RecorderOptions): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("This browser does not expose microphone capture APIs.");
    }

    if (!window.isSecureContext && location.hostname !== "localhost") {
      throw new Error("Microphone capture requires HTTPS or localhost.");
    }

    this.onChunk = options.onChunk;
    this.onLevel = options.onLevel;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true
      }
    });

    const AudioContextClass =
      window.AudioContext ??
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioContextClass) {
      throw new Error("This browser does not support Web Audio.");
    }

    this.audioContext = new AudioContextClass();
    await this.audioContext.audioWorklet.addModule("/audio-chunk-processor.js");

    this.source = this.audioContext.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.audioContext, "audio-chunk-processor", {
      processorOptions: {
        chunkSeconds: options.chunkSeconds
      }
    });
    this.zeroGain = this.audioContext.createGain();
    this.zeroGain.gain.value = 0;

    this.node.port.onmessage = (event: MessageEvent) => {
      if (event.data?.type === "level") {
        this.onLevel?.(Number(event.data.rms) || 0);
        return;
      }

      if (event.data?.type !== "chunk") {
        return;
      }

      const chunk = event.data as Omit<AudioChunk, "id">;
      const id =
        globalThis.crypto?.randomUUID?.() ??
        `${Date.now()}-${chunk.sequence}-${Math.random().toString(16).slice(2)}`;

      this.onChunk?.({
        id,
        sequence: chunk.sequence,
        sampleRate: chunk.sampleRate,
        samples: chunk.samples,
        rms: chunk.rms,
        durationSeconds: chunk.durationSeconds
      });
    };

    this.source.connect(this.node);
    this.node.connect(this.zeroGain);
    this.zeroGain.connect(this.audioContext.destination);
  }

  async stop(): Promise<void> {
    this.node?.port.postMessage({ type: "flush" });
    await new Promise((resolve) => window.setTimeout(resolve, 80));

    this.source?.disconnect();
    this.node?.disconnect();
    this.zeroGain?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());

    if (this.audioContext && this.audioContext.state !== "closed") {
      await this.audioContext.close();
    }

    this.audioContext = undefined;
    this.source = undefined;
    this.node = undefined;
    this.zeroGain = undefined;
    this.stream = undefined;
  }
}

export function resampleTo16Khz(samples: Float32Array, sampleRate: number): Float32Array {
  if (sampleRate === TARGET_SAMPLE_RATE) {
    return samples.slice();
  }

  const ratio = sampleRate / TARGET_SAMPLE_RATE;
  const outputLength = Math.max(1, Math.round(samples.length / ratio));
  const output = new Float32Array(outputLength);

  for (let index = 0; index < outputLength; index += 1) {
    const sourceIndex = index * ratio;
    const leftIndex = Math.floor(sourceIndex);
    const rightIndex = Math.min(samples.length - 1, leftIndex + 1);
    const weight = sourceIndex - leftIndex;
    const left = samples[leftIndex] ?? 0;
    const right = samples[rightIndex] ?? left;
    output[index] = left + (right - left) * weight;
  }

  return output;
}

export function rmsToPercent(rms: number): number {
  const scaled = Math.min(1, Math.max(0, rms * 18));
  return Math.round(scaled * 100);
}
